import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Sparkles, Plus, Search, Phone, MapPin, Truck, Pencil, Archive, RotateCcw, Loader2, Inbox, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";
import TerritoryUnitSelect from "../../components/common/TerritoryUnitSelect";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import StickyHeading from "../../components/common/StickyHeading";
import { joinParts, plural } from "../../lib/vocabulary";

const ALL = "__all__";

// D11: "Distributors: admin & above create/edit/delete; sales use-only."
//
// THE SALES MANAGER WAS STILL IN THIS LIST. `routes_distributors` took his
// write access away deliberately when D11 was answered — its docstring says so
// in as many words — and this page went on offering him "Add distributor",
// "Edit" and a form that 403s on save. A button that cannot succeed is worse
// than an absent one: it reads as a right he has and a system that is broken.
// One spelling of the rule, and it is the backend's.
const DISTRIBUTOR_ADMIN_ROLES = ["admin_manager", "admin", "director"];

export default function DistributorsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = DISTRIBUTOR_ADMIN_ROLES.includes(role);
  const canDelete = ["admin", "director"].includes(role);

  // The dashboard tile drills in here (F288). The filter is a URL parameter so
  // the tile can open the punch list directly rather than landing the reader
  // on a full list with the gap nowhere in sight — the inert-tile half of
  // RC-15.
  const [params, setParams] = useSearchParams();
  const needsTerritoryOnly = params.get("needs_territory") === "1";

  const [gap, setGap] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState(ALL);
  const [editTarget, setEditTarget] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const query = {};
      if (search.trim()) query.search = search.trim();
      if (areaFilter !== ALL) query.area = areaFilter;
      if (showArchived) query.include_inactive = true;
      if (needsTerritoryOnly) query.needs_territory = true;
      const { data } = await api.get("/distributors", { params: query });
      setRows(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, areaFilter, showArchived, needsTerritoryOnly]);

  // RC-23: seven distributors were visible to every officer's empty list and to
  // nobody's real one. The COUNT is served by the endpoint built for it, and
  // the rows behind it come from the same predicate — so the banner and the
  // list cannot describe two populations. Only the roles that can assign a
  // unit fetch it; for anyone else the endpoint refuses, which is why this is
  // gated on `canEdit` rather than on a 403 being swallowed.
  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    api.get("/distributors/needs-territory")
      .then(({ data }) => { if (!cancelled) setGap(data); })
      // A gap count that fails to load must not render as "no gap". It renders
      // as nothing, and the list below is unaffected.
      .catch(() => { if (!cancelled) setGap(null); });
    return () => { cancelled = true; };
  }, [canEdit, rows]);

  const areas = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.area && s.add(r.area));
    return Array.from(s).sort();
  }, [rows]);

  async function handleDeactivate() {
    if (!deactivateTarget) return;
    try {
      const { data } = await api.delete(`/distributors/${deactivateTarget.id}`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Distributor archived");
      setDeactivateTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function handleRestore(d) {
    try {
      const { data } = await api.put(`/distributors/${d.id}`, { is_active: true });
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Distributor restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="distributors-page">
      <StickyHeading
        icon={Sparkles}
        label="Distributors"
        title={role === "sales_officer" ? "Distributors in your territory" : "Distributor master"}
        subtitle={
          <>
            {joinParts([
              plural(rows.length, "distributor"),
              areas.length > 0 ? plural(areas.length, "area") : null,
            ])}
          </>
        }
        testId="distributors-heading"
        actions={
          canEdit && (
            <Button
              onClick={() => setOpenCreate(true)}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
              data-testid="distributors-create-button"
            >
              <Plus size={14} className="mr-1.5" />
              Add distributor
            </Button>
          )
        }
      />

      {/* RC-23, SHOWN AS A GAP. A distributor with no territory unit is on the
          master and in front of nobody: `_matches_territory` cannot invent
          visibility for it, and refusing to invent it is the correct behaviour
          — so the record has to be visible AS A GAP instead, to the people who
          can close it. The number and the rows behind it come from one
          predicate on the backend (`NO_UNIT_MATCH`), which is why this banner
          is not free to compute its own. */}
      {canEdit && gap && gap.total > 0 && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 md:px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          data-testid="distributors-gap-banner"
        >
          <div className="flex items-start gap-2.5 min-w-0">
            <EyeOff className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {plural(gap.total, "distributor")} visible to no Sales Officer
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                They carry no territory unit, so no officer's list can reach
                them. Assign a unit on each record to close the gap.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              const next = new URLSearchParams(params);
              if (needsTerritoryOnly) next.delete("needs_territory");
              else next.set("needs_territory", "1");
              setParams(next);
            }}
            className="h-9 shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
            data-testid="distributors-gap-toggle"
          >
            {needsTerritoryOnly ? "Show all distributors" : "Show only these"}
          </Button>
        </div>
      )}

      <div className="bg-white border border-callisto rounded-xl p-3 md:p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-callisto-muted" />
          <Input
            placeholder="Search name, contact, city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 pl-9 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="distributors-search"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="h-11 flex-1 rounded-md border border-callisto bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-callisto-gold"
            data-testid="distributors-area-filter"
          >
            <option value={ALL}>All areas</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {canDelete && (
          <label className="sm:col-span-2 inline-flex items-center gap-2 text-xs text-callisto-body cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-callisto accent-callisto-navy"
              data-testid="distributors-show-archived"
            />
            Show archived distributors
          </label>
        )}
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center" data-testid="distributors-empty">
          <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
            <Inbox className="h-6 w-6 text-callisto-navy" />
          </div>
          <p className="text-sm font-semibold text-callisto-navy">
            {needsTerritoryOnly
              ? "Every distributor has a territory unit"
              : "No distributors found"}
          </p>
          <p className="mt-1 text-xs text-callisto-muted">
            {/* "Nothing left to do" and "nothing here" both render an empty
                list, and only one of them is good news. The filtered view says
                which one it is. */}
            {needsTerritoryOnly
              ? "Nothing is invisible to the field. Clear the filter to see the full master."
              : role === "sales_officer"
                ? "No distributors mapped to your territory yet."
                : "Click \"Add distributor\" to onboard your first one."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="distributors-grid">
          {rows.map((d) => (
            <DistributorCard
              key={d.id}
              d={d}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => setEditTarget(d)}
              onDeactivate={() => setDeactivateTarget(d)}
              onRestore={() => handleRestore(d)}
            />
          ))}
        </div>
      )}

      <DistributorFormDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSaved={() => { setOpenCreate(false); load(); }}
      />
      <DistributorFormDialog
        open={!!editTarget}
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); load(); }}
      />

      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => !o && setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive distributor?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-callisto-navy">{deactivateTarget?.name}</span> will be hidden from the distributor list. The record is retained — tick “Show archived” to find and restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-red-600 hover:bg-red-700" data-testid="distributors-deactivate-confirm">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DistributorCard({ d, canEdit, canDelete, onEdit, onDeactivate, onRestore }) {
  return (
    <div
      className={`bg-white border rounded-xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        d.is_active ? "border-callisto" : "border-slate-200 opacity-70"
      }`}
      data-testid={`distributor-card-${d.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-callisto-navy text-callisto-gold flex items-center justify-center shrink-0">
          <Truck size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-callisto-navy truncate">{d.name}</h3>
          {d.contact_person && <p className="text-[12px] text-callisto-muted truncate">{d.contact_person}</p>}
          <div className="mt-2 space-y-1 text-[12px] text-callisto-body">
            {/* THE WARNING FIRED ON THE WRONG HALF (RC-10). Amber for a MISSING
                unit, nothing at all for an INVALID one — so "Patna-Head
                Office", which the migration could not resolve and which makes
                this distributor visible to nobody, rendered in the same plain
                text as "Patna East". <TerritoryLabel/> flags both, and drops
                the separators around an absent area or city rather than
                leaving the " · " that was supposed to follow them. */}
            <p className="flex items-start gap-1.5">
              <MapPin size={11} className="text-callisto-gold shrink-0 mt-0.5" />
              <TerritoryLabel
                value={d.territory_unit}
                unmigrated={d.territory_unmigrated}
                hint="visible to no officer"
                suffixes={[d.area, d.city]}
                testId={`distributor-territory-${d.id}`}
              />
            </p>
            {d.mobile && (
              <p className="flex items-center gap-1.5">
                <Phone size={11} className="text-callisto-gold" />
                <a href={`tel:${d.mobile}`} className="hover:underline">{d.mobile}</a>
              </p>
            )}
            {d.notes && <p className="text-[11px] text-callisto-muted italic">"{d.notes}"</p>}
            {!d.is_active && <p className="text-orange-600 font-semibold text-[11px]">Archived</p>}
          </div>
        </div>
      </div>
      {canDelete && !d.is_active && (
        <div className="mt-3 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRestore}
            className="h-8 flex-1 border-callisto text-callisto-navy text-[11px] font-semibold"
            data-testid={`distributor-restore-${d.id}`}
          >
            <RotateCcw size={11} className="mr-1" /> Restore
          </Button>
        </div>
      )}
      {(canEdit || canDelete) && d.is_active && (
        <div className="mt-3 flex gap-2">
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className="h-8 flex-1 border-callisto text-callisto-navy text-[11px] font-semibold"
              data-testid={`distributor-edit-${d.id}`}
            >
              <Pencil size={11} className="mr-1" /> Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDeactivate}
              className="h-8 flex-1 border-red-200 text-red-700 hover:bg-red-50 text-[11px] font-semibold"
              data-testid={`distributor-deactivate-${d.id}`}
            >
              <Archive size={11} className="mr-1" /> Archive
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function DistributorFormDialog({ open, target, onClose, onSaved }) {
  const isEdit = !!target;
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [mobile, setMobile] = useState("");
  const [area, setArea] = useState("");
  const [territoryUnitId, setTerritoryUnitId] = useState("");
  const [city, setCity] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setName(target.name || "");
      setContact(target.contact_person || "");
      setMobile(target.mobile || "");
      setArea(target.area || "");
      setTerritoryUnitId(target.territory_unit_id || "");
      setCity(target.city || "");
      setNotes(target.notes || "");
    } else {
      setName("");
      setContact("");
      setMobile("");
      setArea("");
      setTerritoryUnitId("");
      setCity("");
      setNotes("");
    }
  }, [open, target]);

  async function save() {
    if (!name.trim()) return toast.error("Name is required");
    if (!area.trim()) return toast.error("Area is required");
    if (!territoryUnitId) return toast.error("Territory unit is required");
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        contact_person: contact.trim(),
        mobile: mobile.trim(),
        area: area.trim(),
        territory_unit_id: territoryUnitId,
        city: city.trim(),
        notes: notes.trim() || null,
      };
      const { data } = isEdit
        ? await api.put(`/distributors/${target.id}`, payload)
        : await api.post("/distributors", payload);
      if (isPendingMasterChange(data)) {
        toast.success(sentForApprovalMessage(data));
      } else {
        toast.success(isEdit ? "Distributor updated" : "Distributor added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="distributor-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit distributor" : "Add a distributor"}</DialogTitle>
          <DialogDescription>Capture the basics — you can always update later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div>
            <Label className="text-callisto-navy">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="distributor-name-input"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">Contact person</Label>
              <Input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="distributor-contact-input"
              />
            </div>
            <div>
              <Label className="text-callisto-navy">Mobile</Label>
              <Input
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="+91-…"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="distributor-mobile-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Territory unit</Label>
            <p className="mt-0.5 mb-1.5 text-[11px] text-callisto-muted">
              Decides which Sales Officers see this distributor.
            </p>
            {/* `valueField="id"` because POST/PUT /distributors take
                `territory_unit_id`, the reference into settings_territories —
                not the name. Without it the picker emitted "Patna North" into
                an id field and every save came back "That territory unit does
                not exist or is archived". */}
            <TerritoryUnitSelect
              value={territoryUnitId}
              onChange={setTerritoryUnitId}
              valueField="id"
              testId="distributor-territory-unit"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              {/* Street/locality address — free text on purpose. It is not a
                  territory and never drives visibility. */}
              <Label className="text-callisto-navy">Area (address)</Label>
              <Input
                value={area}
                onChange={(e) => setArea(e.target.value)}
                placeholder="e.g., Boring Road"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="distributor-area-input"
              />
            </div>
            <div>
              <Label className="text-callisto-navy">City</Label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="distributor-city-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Credit terms, delivery schedule, etc."
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="distributor-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={save}
            disabled={submitting}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="distributor-save"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save changes" : "Add distributor")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
